"use client";

import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import { useMemo } from "react";

/**
 * Recruiters 5010 — KUK "Where our graduates work".
 *
 * Port of kuk-campus-portal `components/organisms/AluminiCareers.tsx`: a heading,
 * a supporting line, and a row of employer logos.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * WHY NOT REUSE — the two existing options both lose something this block needs.
 * Recruiters 1 renders a 24px glyph beside a text label, not a logo; Placements
 * 1 renders logos but has no sub-heading slot, and the sub-heading ("Top
 * companies trust us to deliver top talent.") is half the message. This variant
 * is on the `recruiters` section because that table already carries the
 * `title` / `sub_title` pair plus a `metadata` array — no new columns needed.
 *
 * TWO ROWS ON MOBILE — v1 splits the logos at `ceil(n / 2)` and renders two
 * centred rows below `tab`, then a single row above it. Reproduced exactly: an
 * odd count puts the extra logo in the FIRST row, which is what `ceil` does and
 * what the reference ships.
 *
 * LOGO BOX — the logos are supplied at wildly different aspect ratios (a square
 * Microsoft tile next to a wide TCS wordmark), so every one sits in a fixed
 * square box with `object-contain`. That is v1's approach and the reason the row
 * reads as even despite the source art; it is not something to "clean up" into
 * intrinsic sizing.
 *
 * DROPPED — v1's `grid-cols-5` on the desktop row, which hard-codes five logos
 * and silently starts a second row at six while leaving a gap at four. A centred
 * flex row wraps at any count and is identical at five, which is what every
 * reference page ships.
 *
 * THE BAND GRADIENT is carried over verbatim. It reaches this app as a plain
 * inline `linear-gradient` rather than a Tailwind utility: v1 defines
 * `bg-alumini-career-gradient` in its own tailwind.config.ts, which this app has
 * no equivalent of, but the VALUE is ordinary CSS and needs no config to render.
 * (An earlier pass here dropped it to a flat tint on the mistaken grounds that
 * the utility "does not exist in this app" — the utility does not, the gradient
 * does.) An uploaded background image overrides it; `background_color` is
 * painted underneath both as the fallback.
 */

/** v1 `bg-alumini-career-gradient`, copied from its tailwind.config.ts. */
const BAND_GRADIENT =
  "linear-gradient(147.8deg, #E9D6DB -1.85%, #E3B0B3 26.8%, #DD9696 63.54%, #9C4C99 122.71%), " +
  "linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))";

/** v1: `w-[100px] h-[100px]` below `tab`, `w-[150px] h-[150px]` above. */
const LOGO_BOX_MOBILE = 100;
const LOGO_BOX_DESKTOP = 150;

interface RecruiterRow {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Recruiters5010Data {
  status?: boolean | number | string;
  title?: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  background_color?: string;
  /** Optional band art, e.g. the reference's gradient exported as an image. */
  desktop_bg_image?: string;
  mobile_bg_image?: string;
  alt_image?: string;
  metadata?: RecruiterRow[];
}

const Recruiters5010 = (props: { data?: Recruiters5010Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | Recruiters5010Data
    | undefined;

  const {
    title = "",
    title_text_color = "#1F2937",
    sub_title = "",
    sub_title_text_color = "#374151",
    background_color = "#F5E4E7",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const logos = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false && !!r?.meta_image)
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || logos.length === 0) return null;

  // v1: `Math.ceil(n / 2)` — an odd count puts the extra logo in the first row.
  const mid = Math.ceil(logos.length / 2);
  const firstRow = logos.slice(0, mid);
  const secondRow = logos.slice(mid);

  /**
   * `withShadow` is not decoration: v1 puts `shadow-sm` on its MOBILE logo cells
   * and nothing on its desktop ones. Applying it at every width draws a faint
   * box round each desktop logo that reads as a column divider the reference
   * does not have.
   */
  const logoBox = (
    row: RecruiterRow,
    key: string,
    size: number,
    withShadow: boolean,
  ) => (
    <div
      key={key}
      className={cn(
        "flex items-center justify-center p-2",
        // v1 puts `mx-4` on the desktop cells; the mobile rows use `gap-4`.
        !withShadow && "tab:mx-4",
        withShadow && "shadow-sm",
      )}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <Image
        src={row.meta_image as string}
        alt={row.meta_alt_image || row.meta_title || ""}
        width={size}
        height={size}
        loading="lazy"
        quality={85}
        className="size-full object-contain"
      />
    </div>
  );

  return (
    <section
      className={cn(
        "flex w-full flex-col items-center justify-center overflow-hidden p-8 shadow-lg",
        "bg-cover bg-center bg-no-repeat",
      )}
      style={{
        backgroundColor: background_color,
        backgroundImage:
          desktop_bg_image || mobile_bg_image
            ? `url("${(desktop_bg_image || mobile_bg_image).replace(/"/g, "%22")}")`
            : BAND_GRADIENT,
      }}
      aria-label={alt_image || undefined}
    >
      {title ? (
        <h2
          className="mb-5 mt-6 text-center text-2xl font-bold"
          style={{ color: title_text_color }}
        >
          {title}
        </h2>
      ) : null}

      {sub_title ? (
        <p className="mb-6 text-center" style={{ color: sub_title_text_color }}>
          {sub_title}
        </p>
      ) : null}

      <div className="relative w-full overflow-hidden">
        {/* Below tab: two centred rows. */}
        <div className="grid gap-4 tab:hidden">
          <div className="flex flex-wrap justify-center gap-4">
            {firstRow.map((row, i) => logoBox(row, `r1-${row.id ?? i}`, LOGO_BOX_MOBILE, true))}
          </div>
          {secondRow.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-4">
              {secondRow.map((row, i) =>
                logoBox(row, `r2-${row.id ?? i}`, LOGO_BOX_MOBILE, true),
              )}
            </div>
          ) : null}
        </div>

        {/* tab and up: one centred row that wraps at any count. */}
        {/* v1: `grid grid-cols-5 px-24` with `mx-4` on each cell — 16px either
            side, so 32px between logos and the row spread evenly across the
            container. A centred flex row with `gap-8` distributes them
            differently and reads as the wrong spacing. */}
        <div className="hidden items-center justify-center px-24 tab:grid tab:grid-cols-5">
          {logos.map((row, i) => logoBox(row, `d-${row.id ?? i}`, LOGO_BOX_DESKTOP, false))}
        </div>
      </div>
    </section>
  );
};

export default Recruiters5010;
